// React Imports
import React, { useEffect, useState } from "react";

// Internationalization Import
import i18n from "i18next";
// Internal Imports
import { IUserDocument } from "../../Models/IUserDocument";
import { MediathekColumns } from "./MediathekColumns";
import { helperMethods } from "../../helpers/helperMethods";
import DownloadNotification from "../../Controls/DownloadDialog";
import DocumentViewer from "../../Controls/DocumentViewer";
import { baseUrl } from "../../helpers/constants";
import DeleteMediathekDocumentDialog from "./DeleteMediathekDocumentDialog";
import FreigabeDialog from "../FreigabeDialog/FreigabeDialog";
import MediathekShortLinkDialog from "./MediathekShortLinkDialog";
import LuebPagedDataGrid from "../../Controls/LuebPagedDataGrid";
import {
  MessageBar,
  ProgressBar,
  Spinner,
  Text,
} from "@fluentui/react-components";

// Props
interface IMediathekResultProps {
  documents: IUserDocument[];
  isLoading: boolean;
  isAuthenticated: boolean;
  onReloadRequested: any;
}

// Function
function MediathekResult(props: IMediathekResultProps) {
  const [documents, setDocuments] = useState<IUserDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState<boolean>(false);
  const [showDownloadNotification, setShowDownloadNotification] =
    useState(false);

  const [showShortLinkDialog, setShowShortLinkDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showFreigabeDialog, setShowFreigabeDialog] = useState(false);

  const [documentProps, setDocumentProps] = useState({ path: "", title: "" });

  useEffect(() => {
    setLoadingDocs(props.isLoading);
  }, [props.isLoading]);

  useEffect(() => {
    setDocuments(props.documents);
  }, [props.documents]);

  const columns = MediathekColumns();

  useEffect(() => {
    if (columns.documentToDownload) {
      setShowDownloadNotification(true);
      helperMethods
        .downloadFile(columns.documentToDownload, props.isAuthenticated)
        .finally(() => {
          setShowDownloadNotification(false);
          columns.resetDocumentToDownload();
        });
    }
  }, [columns.documentToDownload]);

  useEffect(() => {
    if (columns.documentToView) {
      setDocumentProps({
        path: columns.documentToView.docPath.replace(
          "~",
          baseUrl + "/api/documents/GetDocumentFromPath?path=",
        ),
        title: columns.documentToView.docFileName,
      });
    }
  }, [columns.documentToView]);

  useEffect(() => {
    if (columns.documentToDelete) {
      setShowDeleteDialog(true);
    } else {
      setShowDeleteDialog(false);
    }
  }, [columns.documentToDelete]);

  useEffect(() => {
    if (columns.documentToShare) {
      setShowFreigabeDialog(true);
    } else {
      setShowFreigabeDialog(false);
    }
  }, [columns.documentToShare]);

  useEffect(() => {
    if (columns.shortLink) {
      setShowShortLinkDialog(true);
    }
  }, [columns.shortLink]);

  return (
    <div className="lueb-ContentBlock">
      {showDownloadNotification && <DownloadNotification />}

      <MediathekShortLinkDialog
        link={columns.shortLink}
        onDismiss={() => {
          setShowShortLinkDialog(false);
          columns.resetShortLink();
        }}
        isDialogOpen={showShortLinkDialog}
      />

      <DeleteMediathekDocumentDialog
        isDialogOpen={showDeleteDialog}
        document={
          columns.documentToDelete ? columns.documentToDelete : undefined
        }
        onDismiss={() => columns.resetDocumentToDelete()}
        onDelete={() => {
          props.onReloadRequested();
          columns.resetDocumentToDelete();
        }}
      />

      <FreigabeDialog
        onSave={() => {
          columns.resetDocumentToShare();
        }}
        onDismiss={() => {
          columns.resetDocumentToShare();
        }}
        isDialogOpen={showFreigabeDialog}
        objectGuid={
          columns.documentToShare ? columns.documentToShare.userObjectGuid : ""
        }
        objectType="UserDocument"
        referenceGuid=""
      />

      <DocumentViewer
        path={documentProps?.path}
        title={documentProps?.title}
        onDismiss={() => {
          columns.resetDocumentToView();
          setDocumentProps({ path: "", title: "" });
        }}
        canDownload={true}
      />
      <LuebPagedDataGrid
        columns={columns.columns}
        items={documents}
        resizableColumns={true}
        columnSizingOptions={{
          extensionId: { defaultWidth: 60, minWidth: 60 },
          nameId: { defaultWidth: 350, minWidth: 340 },
          actionsId: { defaultWidth: 50, minWidth: 40 },
          docDateId: { defaultWidth: 120, minWidth: 120 },
          sprachenId: { defaultWidth: 120, minWidth: 120 },
          docGroupId: { defaultWidth: 150, minWidth: 150 },
          validUntil: { defaultWidth: 120, minWidth: 120 },
        }}
      />
      {loadingDocs == true && (
        <div style={{ marginTop: "10px", marginBottom: "10px" }}>
          <ProgressBar thickness="large" />
        </div>
      )}
      {documents.length < 1 && (
        <MessageBar intent="info">{i18n.t<string>("noItemsFound")}</MessageBar>
      )}
    </div>
  );
}

// Export Function
export default MediathekResult;
